import { useCallback, useState } from 'react'
import {
  CheckStateProvider,
  fancyRowRootClassName,
  Table,
  useCopyColumnsWidths,
  useTopShiftState,
} from 'app/common/ui-components/table'
import Sticky from 'app/common/templates-next/sticky'

import { TableHeader } from './TableHeader'
import {
  useColumnProps,
  useColumnsArranged,
  useColumnTitles,
  useHeaderColumnProps,
} from './columns'
import { TableRow } from './TableRow'

import styles from './DataTable.module.scss'
import SimpleDate from 'app/common/classes/simple-date'
import { EncashmentsRowItemType } from 'app/dataTypes/reports/data/finances/encashments'
import {
  EncashmentsAction,
  EncashmentsStatusAction,
} from 'app/dataTypes/reports/data/finances/encashments/action'

type Props = {
  data: Array<EncashmentsRowItemType>
  date: SimpleDate
  currency: string
  onChangeStatus: (params: EncashmentsStatusAction) => Promise<boolean>
  selectionMode: null | EncashmentsAction
  selection: Set<string>
  onChangeSelection: (orgID: string, checked: boolean) => void
  onChangeSelectionAll: (selectedAll: boolean) => void
  selectedAll: boolean
  showSelectAllCheckbox: boolean
  hasStickyHeader: boolean
}

export const DataTable = ({
  data,
  currency,
  date,
  onChangeStatus,
  selectionMode,
  selection,
  onChangeSelection,
  onChangeSelectionAll,
  selectedAll,
  showSelectAllCheckbox,
  hasStickyHeader,
}: Props) => {
  const columns = useColumnsArranged()
  const titles = useColumnTitles(currency)
  const headerColumnProps = useHeaderColumnProps()
  const columnProps = useColumnProps()

  const [updateRowNode, calcTopShift, nodes] = useTopShiftState()

  const [updateKey, setUpdateKey] = useState([])
  const [trFrom, trTo] = useCopyColumnsWidths(updateKey, { useClassIndexes: true })
  const updateColumnsWidths = useCallback(() => {
    setUpdateKey([])
  }, [])

  return (
    <Table wide className={styles.root}>
      <thead>
        <Sticky ref={trFrom} resetShift={!hasStickyHeader} applyToChildrenTh>
          {(ref, stuck, shadow) => (
            <TableHeader
              ref={ref}
              columns={columns}
              titles={titles}
              stuck={stuck}
              shadow={shadow}
              columnProps={headerColumnProps}
              onChangeSelectionAll={onChangeSelectionAll}
              selectedAll={selectedAll}
              showSelectAllCheckbox={showSelectAllCheckbox}
            />
          )}
        </Sticky>
      </thead>

      <tbody className={fancyRowRootClassName}>
        <CheckStateProvider>
          {Array.from(data.values()).map(({ encashmentsSummary, encashmentsDetails }, index) => (
            <TableRow
              key={encashmentsSummary.schoolId + encashmentsSummary.salePointId}
              ID={encashmentsSummary.schoolId + encashmentsSummary.salePointId}
              columns={columns}
              disabled={false}
              columnProps={columnProps}
              encashmentsSummary={encashmentsSummary}
              encashmentsDetails={encashmentsDetails}
              date={date}
              index={index}
              calcTopShift={calcTopShift}
              trElem={nodes[String(index)]}
              updateRowNode={updateRowNode}
              onChangeStatus={onChangeStatus}
              innerTableHeaderRef={trTo}
              selectionMode={selectionMode}
              selection={selection}
              onChangeSelection={onChangeSelection}
              onRowDataUpdated={updateColumnsWidths}
            />
          ))}
        </CheckStateProvider>
      </tbody>
    </Table>
  )
}
